import { ObjectId } from '@racemap/sdk/schema/base';
import type { AnalyticsEvents } from '@racemap/utilities/analytics/registry';
import { capitalize } from '@racemap/utilities/functions/utils';
import type { Location } from 'react-router';
import shortHash from 'shorthash2';
import { isNewBuildAvailable } from './buildState';
import { sanitizeLocation } from './gtag';

/** `starters.3.name` → `starters.#.name`: one GA row per setting, not per array entry. */
export const settingKey = (path: string): string => path.replace(/\.\d+(?=\.|$)/g, '.#');

/** A short alphanumeric extension or '': an extensionless name may itself be personal. */
export const fileExtension = (name: string): string => {
  const ext = name.includes('.') ? (name.split('.').pop() ?? '').toLowerCase() : '';
  return /^[a-z0-9]{1,8}$/.test(ext) ? ext : '';
};

export function trackEvent<K extends keyof AnalyticsEvents>(
  name: K,
  ...params: AnalyticsEvents[K] extends undefined ? [] : [AnalyticsEvents[K]]
): void {
  if (window.gtag === undefined) return;
  window.gtag('event', name, params[0]);
}

const getAggregatedTitle = (pathname: string) => {
  const parts = pathname.split('/').filter((part) => part.length > 0);
  return `Racemap | Admin | ${parts
    .filter((p) => p !== 'admin')
    .map((p) => (ObjectId.isValid(p) ? '#' : capitalize(p)))
    .join(' | ')}`;
};

export function sendPageViewEvent(location: Location) {
  if (window.gtag === undefined) return;

  // `set` so later events on this route carry the redacted URL too
  window.gtag('set', {
    page_location: sanitizeLocation(window.location.origin + location.pathname + location.search),
  });
  window.gtag('event', 'page_view', { page_title: getAggregatedTitle(location.pathname) });
}

export type EventContext = Record<string, string | number | boolean>;

/**
 * The message is hashed rather than sent: it routinely carries event ids, e-mail addresses and
 * URLs, none of which belong in GA. The hash still groups repeats of the same error, and
 * `error_name` is safe to send in the clear and usually identifies the failure class.
 *
 * `build_version` is what makes "how often does this happen" answerable: without it there is no
 * way to tell an error on the current build from one in a tab that has been open for weeks.
 */
export function sendErrorEvent(error: unknown, fatal = false, context: EventContext = {}) {
  if (window.gtag === undefined) return;

  const errorName = error instanceof Error ? error.name : 'UnknownError';
  const errorMessage = error instanceof Error ? error.message : 'UnknownError';
  const errorHash = shortHash(errorMessage);

  gtag('event', 'exception', {
    description: `${errorName} - ${errorHash}`,
    error_name: errorName,
    build_version: window.__BUILD_VERSION__ ?? 'unknown',
    is_stale: isNewBuildAvailable(),
    route: getAggregatedTitle(window.location.pathname),
    fatal,
    ...context,
  });
}
